// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  getAuth
} from 'firebase/auth';
import { tutoresApi, usuariosApi } from '../services/api';
import { auth } from '../config/firebase';
import { connectSocket, disconnectSocket } from '../services/socket';
import { isClientes } from '../config/constants';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [nombreEmpresa, setNombreEmpresa] = useState(() => localStorage.getItem('nombreEmpresa') || 'DUFY ASESORIAS');

  // Función para iniciar sesión
  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      try {
        //para userData, clientes o usuarios tutores o admins, colabos
        let userData ;
        console.log("is clientes",isClientes);
        
        userData = await usuariosApi.getById(userCredential.user.uid);
        
        // Guardar en localStorage para persistencia adicional si lo deseas
        localStorage.setItem('currentUserData', JSON.stringify(userData));
        
        await connectSocket();
        
        setCurrentUserData(userData);
        setError(null);
      } catch (apiError) {
        console.error("Error al obtener datos del usuario desde API:", apiError);
        setError('Error al obtener datos del usuario: ' + apiError.message);
      }
      
      return userCredential;
    } catch (error) {
      setError('Error al iniciar sesión: ' + error.message);
      throw error;
    }
  }

  // Función para cerrar sesión
  async function logout() {
    try {
      await signOut(auth);
      localStorage.removeItem('currentUserData');
      setCurrentUserData(null);
      // ✅ NUEVO: Limpia la empresa al cerrar sesión
      localStorage.removeItem('nombreEmpresa');
      disconnectSocket('');
      setError(null);
    } catch (error) {
      setError('Error al cerrar sesión: ' + error.message);
      throw error;
    }
  }

  function guardarEmpresa(empresa) {
    localStorage.setItem('nombreEmpresa', empresa);
    setNombreEmpresa(empresa);
  }

  // Función para crear un nuevo usuario
  async function signup(email, password, userData) {
    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Crear el usuario en tu API con los datos adicionales
      if (userData) {
        const newUserData = {
          ...userData,
          uid: userCredential.user.uid,
          email: userCredential.user.email
        };
        
        await tutoresApi.create(newUserData);
        
        // Actualizar el estado con los datos del usuario
        setCurrentUserData(newUserData);
        localStorage.setItem('currentUserData', JSON.stringify(newUserData));
      }
      
      return userCredential;
    } catch (error) {
      setError('Error al registrar usuario: ' + error.message);
      throw error;
    }
  }

  //funcion para cambiar contraseña
  function verifyResetCode(code) {
    return verifyPasswordResetCode(auth, code);
  }

  //aplicar cambio de contraseña
  function confirmReset(code, newPassword) {
    return confirmPasswordReset(auth, code, newPassword);
  }

  // Función para actualizar datos del usuario
  async function updateUserData(userData) {
    try {
      if (!currentUser) throw new Error('No hay usuario autenticado');
      
      const updatedUserData = await tutoresApi.update(currentUser.uid, userData);
      
      // Actualizar los datos en el estado y localStorage
      setCurrentUserData(updatedUserData);
      localStorage.setItem('currentUserData', JSON.stringify(updatedUserData));
      
      return updatedUserData;
    } catch (error) {
      setError('Error al actualizar datos del usuario: ' + error.message);
      throw error;
    }
  }

  //enviar mensaje de verificacion
  async function setupSMSverification(numero, contenedorElemento) {
    try {
      auth.languageCode = 'es';

      // 1. Limpiar instancia previa
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }

      // 2. Crear instancia (Asegúrate de que 'auth' venga de getAuth())
      window.recaptchaVerifier = new RecaptchaVerifier(auth, contenedorElemento, {
        'size': 'invisible'
      });

      const cleanPhone = numero.replace(/\D/g, '');
      const phoneWithPrefix = cleanPhone.startsWith('57') ? `+${cleanPhone}` : `+57${cleanPhone}`;

      // 3. Ejecutar envío
      // El error 400 suele morir aquí si la API Key de Firebase no tiene permisos de SMS
      return await signInWithPhoneNumber(auth, phoneWithPrefix, window.recaptchaVerifier);

    } catch (error) {
      console.error("Error en Firebase Auth:", error.code);
      if (error.code === 'auth/invalid-app-credential') {
        console.warn("REVISA: ¿Has habilitado el método de Teléfono en la consola de Firebase?");
      }
      throw error;
    }
  }

  useEffect(() => {
    // Suscripción a cambios en el estado de autenticación de Firebase
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        //console.log("buscando autenticado");
        // Si hay un usuario autenticado, intentar obtener sus datos desde el localStorage primero
        const storedUserData = localStorage.getItem('currentUserData');
        if (storedUserData) {
          setCurrentUserData(JSON.parse(storedUserData));
        }
        
        // Luego actualizar desde la API para tener los datos más recientes
        try {
          const freshUserData = await usuariosApi.getById(user.uid);
          setCurrentUserData(freshUserData);
          localStorage.setItem('currentUserData', JSON.stringify(freshUserData));
          await connectSocket();
        } catch (error) {
          console.error("Error al obtener datos actualizados del usuario:", error);
          // No establecemos error aquí para no interrumpir la experiencia si los datos locales son suficientes
        }
      } else {
        console.log("error, no hay autenticado");
        // Si no hay usuario autenticado, limpiar los datos
        setCurrentUserData(null);
        localStorage.removeItem('currentUserData');
      }
      
      //console.log("cagado esta vaina");
      setLoading(false);
      //console.log("el loading es ",loading);
    });

    // La función de limpieza que se ejecuta cuando el componente se desmonta
    return unsubscribe;
  }, []);

  const value = {
    currentUser,         // Usuario de Firebase Auth
    currentUserData,     // Datos adicionales del usuario desde tu API
    login,
    logout,
    signup,
    verifyResetCode,
    confirmReset,
    updateUserData,      // Nueva función para actualizar datos
    guardarEmpresa,
    setupSMSverification,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
